import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { filter, take } from 'rxjs/operators';

import { AuthService } from '../../services/auth.service';
import { ApiConnectivityService } from '../../services/api-connectivity.service';
import { AreaConstants } from '../../constants/area.constants';
import { PermissionConstants } from '../../constants/permission.constants';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { TenantSelectionPanelComponent } from '../../components/tenant-selection-panel/tenant-selection-panel.component';
import { Router } from '@angular/router';
import { ToastService } from '../../toast/toast.service';
import { MinutesSecondsPipe } from '../../pipes/minutes-seconds.pipe';

@Component({
    selector: 'site-header',
    templateUrl: './site-header.component.html',

    styleUrls: [
        './site-header.component.scss'
    ],

    imports: [
        CommonModule,
        HasPermissionDirective,
        TenantSelectionPanelComponent,
        MinutesSecondsPipe
    ],
})
export class SiteHeaderComponent implements OnInit {
    public isAuthenticated = false;
    public userName: string | null = null;
    public tenantName: string | null = null;
    public showTenantPanel = false;
    public tenants: any = null;
    public isApiConnected = true;

    @Input()
    tokenExpirySeconds: number = -1;

    public menuPermissions: any = {
        showDeclaration: false,
        showRenewal: false,
        showPowerBi: false,
        showAdministration: false,
    };

    constructor(
        private router: Router,
        private authService: AuthService,
        private toastService: ToastService,
        private apiConnectivityService: ApiConnectivityService,
    ) {}

    async ngOnInit(): Promise<void> {
        this.subscribeToAuthenticated();
        this.subscribeToPermissionsReady();
        this.subscribeToApiConnectivity();
    }

    private subscribeToApiConnectivity(): void {
        this.apiConnectivityService.isConnected$.subscribe((isConnected) => {
            this.isApiConnected = isConnected;
        });
    }

    private subscribeToAuthenticated(): void {
        this.authService.authenticated$.subscribe(async (isAuthenticated) => {
            this.isAuthenticated = isAuthenticated;

            if (this.isAuthenticated) {
                await this.authService.loadPermissionsIntoLocalStorage();
                await this.authService.loadTenantsIntoLocalStorage();

                // Get user name AFTER tenants loaded (uses userDisplayName from DB)
                this.userName = await this.authService.getUserName();
                this.tenantName = await this.authService.getTenantName();
                this.tenants = await this.authService.getTenants();
            }
        });
    }

    private subscribeToPermissionsReady(): void {
        this.authService.permissionsReady$.pipe(
            filter(ready => ready),
            take(1)
        )
        .subscribe(() => {
            this.setDeclarationPermission();
            this.setRenewalPermission();
            this.setAdminPermission();
        });
    }

    private setDeclarationPermission(): void {
        this.menuPermissions.showDeclaration = this.authService.hasAnyPermission(
            [
                AreaConstants.Annexure,
                AreaConstants.Ageing,
                AreaConstants.Sales,
                AreaConstants.Matching,
                AreaConstants.DeclarationReports
            ],
            PermissionConstants.View
        );
    }

    private setRenewalPermission(): void {
        this.menuPermissions.showRenewal = this.authService.hasAnyPermission([
            AreaConstants.PremiumData,
            AreaConstants.Performance,
            AreaConstants.ClaimData,
            AreaConstants.RenewalReports,
        ],
        PermissionConstants.View);
    }

    private setPowerBiPermission(): void {
        this.menuPermissions.showPowerBi = this.authService.hasAnyPermission(
            [
                AreaConstants.PowerBi,
            ],
            PermissionConstants.View
        );
    }

    private setAdminPermission(): void {
        this.menuPermissions.showAdministration = this.authService.hasAnyPermission([
            AreaConstants.Tenant,
            AreaConstants.Client,
            AreaConstants.PolicyGroup,
            AreaConstants.ExchangeRate,
            AreaConstants.User,
            AreaConstants.Role,
            AreaConstants.AdminReports,
            AreaConstants.Auditing
        ],
        PermissionConstants.View);
    }

    toggleTenantPanel(event: Event): void {
        event.preventDefault();
        this.showTenantPanel = !this.showTenantPanel;
    }

    async onTenantSelected(tenantId: number): Promise<void> {
        this.showTenantPanel = false;

        if (this.tenants) {
            const selectedTenant = this.tenants.find((tenant: any) => tenant.tenantId === tenantId);

            if (selectedTenant) {
                this.tenantName = String(selectedTenant.tenantName);
                this.authService.updateEnabledTenant(selectedTenant);
                this.toastService.show(`Switched to ${String(selectedTenant.tenantName)}`, 'Info');
                this.router.navigate([`/`]);
            }
        }
    }

    closeModal(): void {
        this.showTenantPanel = false;
    }

    login(event: Event): void {
        event.preventDefault();
        this.authService.signIn();
    }

    logout(event: Event): void {
        event.preventDefault();
        this.authService.signOut();
    }

    showExpiryWarning(): boolean {
        // PRODUCTION: show warning only when 15 minutes or less remain.
        return this.tokenExpirySeconds > 0 && this.tokenExpirySeconds <= 900;
    }
}